import { Button, Checkbox, Divider, Form, Input, message, Row, Select, Space, Tooltip } from "antd";
import { getInputRequiredRule, parseDataKey, selectFilterOption } from "src/common/util";
import { Col24, Col8 } from "src/components/Columns";
import { PlusOutlined } from '@ant-design/icons';
import { QuotationEmptyState } from "./QuotationEmptyState";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/state/reducer";
import { serviceLoading } from "src/common/apiLoader/state/selection";
import { GET_CUSTOMERS_API } from "src/features/Customer/state/actionTypes";
import { Customer } from "src/common/models/customer";
import { useEffect, useRef, useState } from "react";
import { getCustomersApi } from "src/features/Customer/state/action";
import { QuotationItemModal } from "./QuotationItemModal";
import { updateCurrentQuotation, upsertQuotationApi } from "../../state/action";
import { ProposalCoverCategoryMode, Quotation, QuotationDetail, QuotationDocumentType, QuotationPolicy, QuotationtStatus } from "src/common/models/quotation";
import { SearchableTable } from "src/components/SearchableTable";
import { policiesTableColumns, summaryColumns, tableColumns } from "./config";
import { QuotationPolicyModal } from "./QuotationPolicyModal";
import { searchableTableUtils } from "src/components/SearchableTable/utils";
import { EditOutlined, DeleteOutlined } from '@ant-design/icons'
import { SystemDescriptions } from "src/common/descriptions";
import { UPSERT_QUOTATION_API } from "../../state/actionTypes";
import { useLocation, useNavigate } from "react-router-dom";
import { Urls } from "src/common/urls";
import { FormActionButtons } from "src/components/FormActionButtons";
import { ServiceCategory } from "src/common/models/serviceCategory";
import { getServiceCategoriesApi } from "src/features/Service/state/action";
import { GET_SERVICE_CATEGORIES_API } from "src/features/Service/state/actionTypes";

export type ReduxProps = {
    customers?: Customer[],
    isLoadingCustomers: boolean,
    isLoadingServiceCategories: boolean,
    isSavingQuotation: boolean,
    currentQuotation?: Partial<Quotation>
    upsertQuotationSuccess: boolean
    serviceCategories?: ServiceCategory[]
}

interface Props {
}

type QuotationDetailTableRow = Omit<QuotationDetail, "characteristics"> & {
    characteristics: string[] | string
}

export const QuotationForm = (props: Props) => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const [form] = Form.useForm();

    const [quotationItemModalOpen, setQuotationItemModalOpen] = useState<boolean>(false)
    const [quotationPolicyModalOpen, setQuotationPolicyModalOpen] = useState<boolean>(false)
    const [selectedDetail, setSelectedDetail] = useState<QuotationDetail | undefined>(undefined)
    const [selectedPolicy, setSelectedPolicy] = useState<QuotationPolicy | undefined>(undefined)
    const [submitClicked, setSubmitClicked] = useState<boolean>(false)
    const hasRequestedCustomers = useRef<boolean>(false)
    const hasRequestedServiceCategories = useRef<boolean>(false)

    const descriptions = SystemDescriptions.PAGES.QUOTATION.CREATE

    const quotationDetailColumnTitles = {
        key: descriptions.DETAIL.TABLE.KEY,
        description: descriptions.DETAIL.TABLE.DESCRIPTION,
        qty: descriptions.DETAIL.TABLE.QTY,
        price: descriptions.DETAIL.TABLE.PRICE,
        total: descriptions.DETAIL.TABLE.TOTAL,
    }

    const isSameDetail = (left?: QuotationDetail, right?: QuotationDetail): boolean => {
        if (!left || !right) return false
        if (left.ref && right.ref) return left.ref === right.ref
        if (left.id && right.id) return left.id === right.id
        return false
    }

    const quotationPolicyColumnTitles = {
        key: descriptions.POLICY.TABLE.KEY,
        name: descriptions.POLICY.TABLE.NAME,
        description: descriptions.POLICY.TABLE.DESCRIPTION,
    }

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        customers: state.customer.customers,
        isLoadingCustomers: serviceLoading(state, [GET_CUSTOMERS_API]),
        isLoadingServiceCategories: serviceLoading(state, [GET_SERVICE_CATEGORIES_API]),
        isSavingQuotation: serviceLoading(state, [UPSERT_QUOTATION_API]),
        currentQuotation: state.quotation.currentQuotation,
        upsertQuotationSuccess: state.quotation.upsertQuotationSuccess,
        serviceCategories: state.service.serviceCategories,
    }))

    const documentType = Form.useWatch("documentType", form) || QuotationDocumentType.QUOTATION
    const proposalCoverCategoryMode = Form.useWatch("proposalCoverCategoryMode", form) || ProposalCoverCategoryMode.ALL

    useEffect(() => {
        if (!hasRequestedCustomers.current && (!reduxProps.customers || reduxProps.customers.length === 0)) {
            hasRequestedCustomers.current = true
            dispatch(getCustomersApi())
        }
    }, [dispatch, reduxProps.customers])

    useEffect(() => {
        if (!hasRequestedServiceCategories.current && (!reduxProps.serviceCategories || reduxProps.serviceCategories.length === 0)) {
            hasRequestedServiceCategories.current = true
            dispatch(getServiceCategoriesApi())
        }
    }, [dispatch, reduxProps.serviceCategories])

    useEffect(() => {
        if (!reduxProps.currentQuotation) {
            return
        }
        form.setFieldsValue({
            name: reduxProps.currentQuotation.name,
            customer: reduxProps.currentQuotation.customerId?.toString(),
            expirationDays: reduxProps.currentQuotation.expirationDays,
            customerName: reduxProps.currentQuotation.customerName || reduxProps.currentQuotation.contact,
            customerPhone: reduxProps.currentQuotation.customerPhone,
            customerEmail: reduxProps.currentQuotation.customerEmail,
            customerNit: reduxProps.currentQuotation.customerNit,
            address: reduxProps.currentQuotation.customerAddress || reduxProps.currentQuotation.address,
            documentType: reduxProps.currentQuotation.documentType || QuotationDocumentType.QUOTATION,
            proposalShowPrices: reduxProps.currentQuotation.proposalShowPrices ?? true,
            proposalCoverCategoryMode: reduxProps.currentQuotation.proposalCoverCategoryMode || ProposalCoverCategoryMode.ALL,
            proposalCoverCategoryIds: reduxProps.currentQuotation.proposalCoverCategoryIds?.map(id => id.toString()),
        })
    }, [form, reduxProps.currentQuotation])

    useEffect(() => {
        if (submitClicked && reduxProps.upsertQuotationSuccess && reduxProps.currentQuotation?.id) {
            setSubmitClicked(false)
            message.success(descriptions.PDF_ACTIONS.SAVE_SUCCESS)
            const quotationPage = Urls.FRONTEND.QUOTATION.EDIT_PAGE(reduxProps.currentQuotation.id)
            if (location.pathname !== quotationPage) {
                navigate(quotationPage, { replace: true })
            }
        }
    }, [reduxProps.upsertQuotationSuccess, reduxProps.currentQuotation?.id, submitClicked, descriptions.PDF_ACTIONS.SAVE_SUCCESS, location.pathname, navigate])

    const TABLE_COLUMNS = [
        ...tableColumns.map(column => ({
            ...column,
            title: quotationDetailColumnTitles[column.key] || column.title,
        })),
        {
            title: descriptions.DETAIL.TABLE.ACTIONS,
            dataIndex: "actions",
            key: "actions",
            align: searchableTableUtils.alignment.centerAlign,
            render: (_: any, record: QuotationDetail) => {
                const handleEditClick = () => {
                    const foundDetail = reduxProps.currentQuotation?.detail?.find(candidate => isSameDetail(candidate, record))
                    setSelectedDetail(foundDetail)
                    setQuotationItemModalOpen(true)
                }

                const handleDeleteClick = (recordToDelete: QuotationDetail) => {
                    const newDetail: QuotationDetail[] = []
                    reduxProps.currentQuotation?.detail?.forEach(detailItem => {
                        if (!isSameDetail(detailItem, recordToDelete)) {
                            newDetail.push(detailItem)
                        }
                    })

                    dispatch(updateCurrentQuotation({
                        detail: parseDataKey(newDetail)
                    }))
                }

                return <Space size="middle">
                    <Tooltip title={"Editar"} >
                        <Button type="primary" ghost shape="circle"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick()}
                        />
                    </Tooltip>
                    <Tooltip title={"Eliminar"} >
                        <Button type="primary" danger ghost shape="circle"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteClick(record)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const POLICIES_TABLE_COLUMNS = [
        ...policiesTableColumns.map(column => ({
            ...column,
            title: quotationPolicyColumnTitles[column.key] || column.title,
        })),
        {
            title: descriptions.POLICY.TABLE.ACTIONS,
            dataIndex: "actions",
            key: "actions",
            align: searchableTableUtils.alignment.centerAlign,
            render: (_: any, record: QuotationPolicy) => {
                const handleEditClick = () => {
                    const foundPolicy = reduxProps.currentQuotation?.policies?.find(candidate => candidate.ref === record.ref)
                    setSelectedPolicy(foundPolicy)
                    setQuotationPolicyModalOpen(true)
                }

                const handleDeleteClick = (ref: string) => {
                    const newPolicies: QuotationPolicy[] = []
                    reduxProps.currentQuotation?.policies?.forEach(policyItem => {
                        if (policyItem.ref !== ref) {
                            newPolicies.push(policyItem)
                        }
                    })

                    dispatch(updateCurrentQuotation({
                        policies: parseDataKey(newPolicies)
                    }))
                }

                return <Space size="middle">
                    <Tooltip title={"Editar"} >
                        <Button type="primary" ghost shape="circle"
                            icon={<EditOutlined />}
                            onClick={() => handleEditClick()}
                        />
                    </Tooltip>
                    <Tooltip title={"Eliminar"} >
                        <Button type="primary" danger ghost shape="circle"
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteClick(record.ref!)}
                        />
                    </Tooltip>
                </Space>
            },
        },
    ]

    const onFinish = (formValues) => {
        const quotation: Partial<Quotation> = {
            id: reduxProps.currentQuotation?.id,
            customerId: formValues.customer ? Number(formValues.customer) : undefined,
            status: QuotationtStatus.DRAFT,
            documentType: formValues.documentType || QuotationDocumentType.QUOTATION,
            name: formValues.name,
            expirationDays: formValues.expirationDays,
            customerName: formValues.customerName,
            customerPhone: formValues.customerPhone,
            customerEmail: formValues.customerEmail,
            customerNit: formValues.customerNit,
            customerAddress: formValues.address,
            contact: formValues.customerName,
            address: formValues.address,
            detail: reduxProps.currentQuotation?.detail || [],
            policies: reduxProps.currentQuotation?.policies || [],
            proposalShowPrices: formValues.proposalShowPrices,
            proposalCoverCategoryMode: formValues.proposalCoverCategoryMode || ProposalCoverCategoryMode.ALL,
            proposalCoverCategoryIds: formValues.proposalCoverCategoryIds?.map(categoryId => Number(categoryId)),
        }

        dispatch(updateCurrentQuotation(quotation))
        dispatch(upsertQuotationApi(quotation))
        setSubmitClicked(true)
    }

    const normalizeText = (value?: string): string => (value || "").trim().toLowerCase()

    const addDetailInfo = (values: string[], value?: string) => {
        const cleanValue = value?.trim()
        if (!cleanValue) {
            return
        }

        const alreadyExists = values.some(item => normalizeText(item) === normalizeText(cleanValue))
        if (!alreadyExists) {
            values.push(cleanValue)
        }
    }

    const getDetailServiceName = (detail: QuotationDetail): string => (
        detail.serviceName?.trim() || detail.description?.trim() || "-"
    )

    const getDetailServiceTitle = (detail: QuotationDetail): string => {
        const serviceName = getDetailServiceName(detail)
        return detail.serviceCategory ? `${detail.serviceCategory} - ${serviceName}` : serviceName
    }

    const getDetailExpandableInfo = (detail: QuotationDetail): string[] | string => {
        const serviceName = getDetailServiceName(detail)
        const detailInfo: string[] = []

        if (normalizeText(detail.description) !== normalizeText(serviceName)) {
            addDetailInfo(detailInfo, detail.description)
        }

        detail.characteristics?.forEach(characteristic => addDetailInfo(detailInfo, characteristic))

        return detailInfo.length > 0 ? detailInfo : "Not Expandable"
    }

    const getQuotationDetailRows = (): QuotationDetailTableRow[] => (
        (reduxProps.currentQuotation?.detail || []).map(detail => ({
            ...detail,
            description: getDetailServiceTitle(detail),
            characteristics: getDetailExpandableInfo(detail),
        }))
    )

    const handleCustomerChange = (e) => {
        const foundCustomer = reduxProps.customers?.find(c => c.id?.toString() === e)
        if (foundCustomer) {
            form.setFieldsValue({
                customerName: foundCustomer.name,
                address: foundCustomer.address,
                customerPhone: foundCustomer.phone,
                customerNit: foundCustomer.nit,
            })
        }
    }

    const renderQuotationDetail = () => (
        <SearchableTable
            showLoader={false}
            items={getQuotationDetailRows()}
            tableColumns={TABLE_COLUMNS}
            summaryColumns={summaryColumns}
            expandableContent="characteristics"
        />
    )

    const renderDetailSection = () => (
        <>
            <Col24>
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }} >
                    <span style={{ fontSize: 16 }} >{descriptions.DETAIL.TITLE}</span>
                    <Button
                        type="link"
                        onClick={() => setQuotationItemModalOpen(true)}
                    >
                        <PlusOutlined /> {descriptions.DETAIL.ADD_ITEM}
                    </Button>
                </div>
            </Col24>
            <Col24>
                {
                    reduxProps.currentQuotation?.detail && reduxProps.currentQuotation.detail.length > 0
                        ? renderQuotationDetail()
                        : <QuotationEmptyState
                            title={descriptions.DETAIL.EMPTY.TITLE}
                            description={descriptions.DETAIL.EMPTY.DESCRIPTION}
                        />
                }
            </Col24>
        </>
    )

    const getButtonStatus = (): boolean => {
        const values = form.getFieldsValue()
        const isProposal = values.documentType === QuotationDocumentType.PROPOSAL
        return reduxProps.isSavingQuotation || !values.name || !values.customerName || (!isProposal && !values.expirationDays) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0 || !reduxProps.currentQuotation?.detail || reduxProps.currentQuotation?.detail.length === 0
    }

    const renderButtons = () => (
        <Col24>
            <FormActionButtons
                cancelText={descriptions.CANCEL_BUTTON}
                actionText={reduxProps.isSavingQuotation ? descriptions.SAVING_BUTTON : descriptions.SAVE_BUTTON}
                onCancel={() => navigate(Urls.FRONTEND.QUOTATION.RECORDS)}
                actionDisabled={getButtonStatus}
                actionLoading={reduxProps.isSavingQuotation}
            />
        </Col24>
    )

    const renderPoliciesDetail = () => (
        <SearchableTable
            showLoader={false}
            items={reduxProps.currentQuotation?.policies || []}
            tableColumns={POLICIES_TABLE_COLUMNS}
        />
    )

    const renderTermAndConditions = () => {
        return <>
            <Col24>
                <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }} >
                    <span style={{ fontSize: 16 }} >{descriptions.POLICY.TITLE}</span>
                    <Button
                        type="link"
                        onClick={() => setQuotationPolicyModalOpen(true)}
                    >
                        <PlusOutlined /> {descriptions.POLICY.ADD_ITEM}
                    </Button>
                </div>
            </Col24>
            <Col24>
                {
                    reduxProps.currentQuotation?.policies && reduxProps.currentQuotation.policies.length > 0
                        ? renderPoliciesDetail()
                        : <QuotationEmptyState
                            title={descriptions.POLICY.EMPTY.TITLE}
                            description={descriptions.POLICY.EMPTY.DESCRIPTION}
                        />
                }
            </Col24>
        </>
    }

    const renderForm = () => (
        <Form
            form={form}
            size="small"
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
            initialValues={{
                documentType: QuotationDocumentType.QUOTATION,
                proposalShowPrices: true,
                proposalCoverCategoryMode: ProposalCoverCategoryMode.ALL,
            }}
        >
            <Row gutter={[16, 8]}>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.NAME.LABEL}
                        name={"name"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.NAME.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.CUSTOMER.LABEL}
                        name={"customer"}
                        validateTrigger="onBlur"
                    >
                        <Select
                            placeholder={descriptions.FORM.CUSTOMER.PLACEHOLDER}
                            options={reduxProps.customers?.map(customer => ({
                                label: customer.name,
                                value: customer.id?.toString()!,
                            }))}
                            loading={reduxProps.isLoadingCustomers}
                            filterOption={selectFilterOption}
                            showSearch
                            onChange={handleCustomerChange}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.CONTACT.LABEL}
                        name={"customerName"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.CONTACT.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.CONTACT.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label="Tipo de documento"
                        name={"documentType"}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={[
                                {
                                    value: QuotationDocumentType.QUOTATION,
                                    label: "Cotizacion"
                                },
                                {
                                    value: QuotationDocumentType.PROPOSAL,
                                    label: "Propuesta comercial",
                                },
                            ]}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.ADDRESS.LABEL}
                        name={"address"}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.ADDRESS.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.PHONE.LABEL}
                        name={"customerPhone"}
                        validateTrigger="onBlur"
                    >
                        <Input placeholder={descriptions.FORM.PHONE.PLACEHOLDER} />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.EMAIL.LABEL}
                        name={"customerEmail"}
                        validateTrigger="onBlur"
                    >
                        <Input placeholder={descriptions.FORM.EMAIL.PLACEHOLDER} />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.NIT.LABEL}
                        name={"customerNit"}
                        validateTrigger="onBlur"
                    >
                        <Input placeholder={descriptions.FORM.NIT.PLACEHOLDER} />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.FORM.EXPIRATION.LABEL}
                        name={"expirationDays"}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.EXPIRATION.PLACEHOLDER}
                            suffix={descriptions.DAYS}
                            type={"number"}
                            min={1}
                        />
                    </Form.Item>
                </Col8>
                {
                    documentType === QuotationDocumentType.PROPOSAL &&
                    <>
                        <Col8>
                            <Form.Item
                                label="Precios en propuesta"
                                name={"proposalShowPrices"}
                                valuePropName="checked"
                            >
                                <Checkbox>Mostrar precios</Checkbox>
                            </Form.Item>
                        </Col8>
                        <Col8>
                            <Form.Item
                                label="Categorias en portada"
                                name={"proposalCoverCategoryMode"}
                                validateTrigger="onBlur"
                            >
                                <Select
                                    options={[
                                        {
                                            value: ProposalCoverCategoryMode.ALL,
                                            label: "Todas"
                                        },
                                        {
                                            value: ProposalCoverCategoryMode.SELECTED,
                                            label: "Seleccionadas",
                                        },
                                        {
                                            value: ProposalCoverCategoryMode.NONE,
                                            label: "Ninguna",
                                        },
                                    ]}
                                />
                            </Form.Item>
                        </Col8>
                        {
                            proposalCoverCategoryMode === ProposalCoverCategoryMode.SELECTED &&
                            <Col8>
                                <Form.Item
                                    label="Seleccionar categorias"
                                    name={"proposalCoverCategoryIds"}
                                    validateTrigger="onBlur"
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Selecciona categorias"
                                        loading={reduxProps.isLoadingServiceCategories}
                                        options={reduxProps.serviceCategories?.map(category => ({
                                            value: category.id?.toString() || "",
                                            label: category.name,
                                        }))}
                                        filterOption={selectFilterOption}
                                        showSearch
                                    />
                                </Form.Item>
                            </Col8>
                        }
                    </>
                }
                <Col24></Col24>
                <Divider />
                {renderDetailSection()}
                <Divider />
                {renderTermAndConditions()}
                <Divider />
            </Row>
            {/* {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {!props.readOnly && renderButtons()} */}
            {renderButtons()}
        </Form>
    )

    const handleItemDetailAccept = (detailItem: QuotationDetail) => {
        setQuotationItemModalOpen(false)

        const newDetail = [...(reduxProps.currentQuotation?.detail ?? [])];

        const index = newDetail.findIndex(item => isSameDetail(item, detailItem));

        if (index >= 0) {
            newDetail[index] = { ...detailItem };
        } else {
            newDetail.push(detailItem);
        }

        dispatch(updateCurrentQuotation({
            detail: parseDataKey(newDetail),
        }))
    }

    const handlePolicyAccept = (policyItem: QuotationPolicy) => {
        setQuotationPolicyModalOpen(false)

        const newPolicies = [...(reduxProps.currentQuotation?.policies ?? [])];
        const index = newPolicies.findIndex(item => item.ref === policyItem.ref);

        if (index >= 0) {
            newPolicies[index] = { ...policyItem };
        } else {
            newPolicies.push(policyItem);
        }

        dispatch(updateCurrentQuotation({
            policies: parseDataKey(newPolicies),
        }))
    }

    return (
        <>
            {renderForm()}
            <QuotationItemModal
                open={quotationItemModalOpen}
                onCancel={() => {
                    setSelectedDetail(undefined)
                    setQuotationItemModalOpen(false)
                }}
                onAccept={handleItemDetailAccept}
                okText={descriptions.DETAIL.FORM.ACCEPT}
                cancelText={descriptions.DETAIL.FORM.CANCEL}
                initialValues={selectedDetail}
            />
            <QuotationPolicyModal
                open={quotationPolicyModalOpen}
                onCancel={() => {
                    setQuotationPolicyModalOpen(false)
                }}
                onAccept={handlePolicyAccept}
                okText={descriptions.POLICY.FORM.ACCEPT}
                cancelText={descriptions.POLICY.FORM.CANCEL}
                initialValues={selectedPolicy}
            />
        </>
    )
}
